/**
 * Testi italiani della pagina «Profilo e sicurezza» (ADR 0030 §3): account,
 * verifica in due passaggi, sessioni, password, registro accessi, telefoni,
 * preferenze.
 */

/** Testi del profilo. */
export const profile = {
  /** Intestazione della pagina. */
  page: {
    /** Titolo. */
    title: 'Profilo e sicurezza',
    /** Che cosa si fa qui. */
    description: 'Il tuo account, la verifica in due passaggi, le sessioni aperte e i telefoni registrati.',
  },

  /** Avviso durante l'accesso come un altro utente. */
  impersonation:
    "Stai usando l'account di un altro utente: le azioni di sicurezza sono spente. Torna al tuo account per usarle.",

  /** Sezione che il server non offre ancora. */
  unavailable: 'Questa sezione non è ancora disponibile.',

  /** Dati dell'account. */
  account: {
    /** Titolo. */
    title: 'Account',
    /** Nome utente. */
    username: 'Nome utente',
    /** Ruolo. */
    role: 'Ruolo',
    /** Piano. */
    plan: 'Piano',
    /** Collegamento all'abbonamento. */
    planLink: 'Apri abbonamento',
    /** Piano non noto. */
    planUnknown: 'Non noto',
  },

  /** Verifica in due passaggi e codici di recupero. */
  mfa: {
    /** Titolo. */
    title: 'Verifica in due passaggi',
    /** Stato attivo. */
    active: 'Attiva',
    /** Stato non attivo. */
    inactive: 'Non attiva',
    /** Che cosa protegge. */
    text: "Ogni accesso chiede anche il codice dell'app di autenticazione.",
    /** Pulsante che apre la registrazione volontaria. */
    enable: 'Attiva',
    /** Titolo dell'invito per chi ha un abbonamento. */
    reminderTitle: 'Attiva la verifica in due passaggi',
    /** Testo dell'invito. */
    reminderText: "Hai un abbonamento: proteggi l'account con il codice dell'app di autenticazione.",
    /** Pulsante (testo chiesto dall'utente: cinque parole, deroga dichiarata). */
    regenerate: 'Genera nuovi codici di recupero',
    /** Titolo della finestra. */
    dialogTitle: 'Nuovi codici di recupero',
    /** Etichetta del campo del codice. */
    code: 'Codice a sei cifre',
    /** Aiuto sotto il campo. */
    codeHelp: "Lo trovi nell'app di autenticazione. I codici di recupero di prima smettono di valere.",
    /** Conferma della finestra. */
    confirm: 'Genera codici',
    /** Codice mancante o malformato. */
    codeInvalid: 'Scrivi le sei cifre del codice.',
    /** Codice rifiutato. */
    codeRejected: 'Codice non valido. Riprova.',
    /** Generazione non riuscita. */
    failed: 'Generazione non riuscita. Riprova.',
    /** Avviso sopra i codici nuovi. */
    shownOnce: 'Conservali in un posto sicuro: non li vedrai più.',
    /** Nome accessibile dell'elenco dei codici. */
    codesLabel: 'Codici di recupero',
    /** Chiusura dopo aver salvato i codici. */
    done: 'Ho salvato i codici',
  },

  /** Sessioni aperte. */
  sessions: {
    /** Titolo. */
    title: 'Sessioni attive',
    /** Colonna del dispositivo. */
    device: 'Dispositivo',
    /** Colonna dell'indirizzo. */
    ip: 'Indirizzo IP',
    /** Colonna dell'apertura. */
    created: 'Aperta il',
    /** Colonna dell'ultimo uso. */
    lastUsed: 'Ultimo uso',
    /** Etichetta della sessione corrente. */
    current: 'Questa sessione',
    /** Dispositivo non noto. */
    unknownDevice: 'Dispositivo non noto',
    /** Pulsante di riga. */
    revoke: 'Esci da questa sessione',
    /** Nome accessibile del pulsante di riga. */
    revokeNamed: 'Esci da questa sessione ({{device}}, {{when}})',
    /** Titolo della conferma. */
    revokeTitle: 'Chiudere la sessione?',
    /** Testo della conferma. */
    revokeText: '{{device}} dovrà accedere di nuovo.',
    /** Esito. */
    revoked: 'Sessione chiusa.',
    /** Pulsante per tutte le altre. */
    revokeOthers: 'Esci dalle altre',
    /** Titolo della conferma. */
    revokeOthersTitle: 'Chiudere le altre sessioni?',
    /** Testo della conferma. */
    revokeOthersText: 'Gli altri dispositivi dovranno accedere di nuovo. Questa sessione resta aperta.',
    /** Esito. */
    revokedOthers: 'Altre sessioni chiuse.',
    /** Comando non riuscito. */
    failed: 'Chiusura non riuscita. Riprova.',
    /** Lettura non riuscita. */
    loadFailed: 'Caricamento delle sessioni non riuscito. Riprova.',
    /** Nessuna sessione. */
    empty: 'Nessuna sessione aperta.',
  },

  /** Cambio della password. */
  password: {
    /** Titolo. */
    title: 'Password',
    /** Nome accessibile del modulo. */
    form: 'Cambio della password',
    /** Password attuale. */
    current: 'Password attuale',
    /** Password nuova. */
    next: 'Nuova password',
    /** Ripetizione. */
    repeat: 'Ripeti la nuova password',
    /** Pulsante. */
    submit: 'Cambia password',
    /** Esito. */
    changed: 'Password cambiata.',
    /** Password attuale mancante. */
    currentEmpty: 'Scrivi la password attuale.',
    /** Password nuova mancante. */
    nextEmpty: 'Scrivi la nuova password.',
    /** Ripetizione diversa. */
    mismatch: 'Le due password non coincidono.',
    /** Password nuova uguale all'attuale. */
    same: 'La nuova password deve essere diversa da quella attuale.',
    /** Rifiuto `invalid_current_password`. */
    invalidCurrent: 'La password attuale non è corretta.',
    /** Rifiuto `password_policy`. */
    policy: 'La nuova password non rispetta le regole. Scegline una più lunga e più varia.',
    /** Cambio non riuscito. */
    failed: 'Cambio non riuscito. Riprova.',
  },

  /** Registro degli accessi. */
  events: {
    /** Titolo. */
    title: 'Registro accessi',
    /** Colonna dell'istante. */
    at: 'Quando',
    /** Colonna dell'evento. */
    type: 'Evento',
    /** Nessun evento. */
    empty: 'Nessun evento registrato.',
    /** Lettura non riuscita. */
    loadFailed: 'Caricamento del registro non riuscito. Riprova.',
    /** Nomi degli eventi noti. */
    types: {
      /** Accesso riuscito. */
      login: 'Accesso',
      /** Accesso rifiutato. */
      login_failed: 'Accesso non riuscito',
      /** Uscita. */
      logout: 'Uscita',
      /** Verifica in due passaggi riuscita. */
      mfa_verified: 'Verifica in due passaggi',
      /** Verifica in due passaggi rifiutata. */
      mfa_failed: 'Verifica in due passaggi non riuscita',
      /** Password cambiata. */
      password_changed: 'Password cambiata',
      password_reset_requested: 'Richiesta di nuova password',
      /** Sessione chiusa da un altro dispositivo. */
      session_revoked: 'Sessione chiusa',
      /** Codici di recupero rigenerati. */
      recovery_codes_regenerated: 'Nuovi codici di recupero',
    },
  },

  /** Telefoni registrati per le notifiche. */
  devices: {
    /** Titolo. */
    title: 'Telefoni registrati',
    /** Colonna del nome. */
    label: 'Telefono',
    /** Colonna del sistema. */
    platform: 'Sistema',
    /** Colonna dell'ultimo contatto. */
    lastSeen: 'Ultimo contatto',
    /** Telefono senza nome. */
    unnamed: 'Telefono senza nome',
    /** Pulsante di riga. */
    remove: 'Rimuovi',
    /** Nome accessibile del pulsante di riga. */
    removeNamed: 'Rimuovi {{name}}',
    /** Titolo della conferma. */
    removeTitle: 'Rimuovere {{name}}?',
    /** Testo della conferma. */
    removeText: "Il telefono non riceve più notifiche finché l'app non lo registra di nuovo.",
    /** Esito. */
    removed: 'Telefono rimosso.',
    /** Rimozione non riuscita. */
    failed: 'Rimozione non riuscita. Riprova.',
    /** Nessun telefono. */
    empty: "Nessun telefono registrato. Accedi dall'app per ricevere le notifiche.",
    /** Lettura non riuscita. */
    loadFailed: 'Caricamento dei telefoni non riuscito. Riprova.',
  },

  /** Preferenze di questo browser. */
  preferences: {
    /** Titolo. */
    title: 'Preferenze',
    /** Dove stanno. */
    text: 'Tema, lingua, tabelle compatte e invio dei messaggi si scelgono nelle impostazioni.',
    /** Collegamento. */
    link: 'Apri le impostazioni',
  },
};

/** Forma dei testi del profilo, usata per tipare le altre lingue. */
export type Profile = typeof profile;
